import { Data, DateUtils, List, Misc, Model, NeoModel } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from "../../../../App/Models/NotificationDuration";
import { AppService, DomainTypes, Types } from '../../../DomainTypes';
import LadderProjection from '../../../Models/Catalogue/Pipeline/LadderProjection';
import LadderProjectionPagedCriteria from '../../../Models/Catalogue/Pipeline/Lookups/LadderProjectionPagedCriteria';
import LadderProjectionDividendCriteria from '../../../Models/Catalogue/Pipeline/Lookups/LadderProjectionDividendCriteria';
import InvestorDividendCriteria from '../../../../Reporting/Reports/Dividends/InvestorDividendCriteria';
import InvestorDividendReport from '../../../../Reporting/Reports/Dividends/InvestorDividendReport';
import IssuerDividendCriteria from '../../../../Reporting/Reports/Dividends/IssuerDividendCriteria';
import DividendBaseCriteria from '../../../../Reporting/Reports/Dividends/DividendBaseCriteria';
import LadderProjectionDividendLookup from '../../../Models/Catalogue/Pipeline/Lookups/LadderProjectionDividendLookup';

@NeoModel
export default class LadderProjectionVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        protected pipelinesApiClient = AppService.get(Types.Domain.ApiClients.CataloguePipeline),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications)) {

        super(taskRunner);
        this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()));
    }

    public criteria = new LadderProjectionPagedCriteria();

    public investorDividendCriteria: InvestorDividendCriteria | null = null;
    public issuerDividendCriteria: IssuerDividendCriteria | null = null;

    public pageManager = new Data.PageManager(this.criteria, LadderProjection, this.pipelinesApiClient.getLadderProjectionsPaged, {
        pageSize: 15,
        initialTaskRunner: this.taskRunner,
        fetchInitial: true,
        allowSort: true,
        sortBy: 'effectiveDate',
        sortAscending: false,
    });

    public newLadderProjection() {
        this.pageManager.data.addNew();
    }

    public saveLadderProjections() {
        this.taskRunner.run(async () => {
            const response = await this.pipelinesApiClient.saveLadderProjections(this.pageManager.data.toJSArray())
            this.pageManager.data.update(response.data);
            this.notifications.addSuccess("Save Successful", "Ladder projections were saved successfully.", NotificationDuration.Standard);
        });
    }

    public beginDividendsImport(isAsset: boolean) {
        if (isAsset) {
            this.issuerDividendCriteria = Object.assign(new IssuerDividendCriteria(), {
                startDate: this.criteria.startDate,
                endDate: this.criteria.endDate
            });
        }
        else {
            this.investorDividendCriteria = Object.assign(new InvestorDividendCriteria(), {
                startDate: this.criteria.startDate,
                endDate: this.criteria.endDate
            });
        }
    }

    public importDividends(dividendCriteriaBase: DividendBaseCriteria, isAsset: boolean) {
        this.taskRunner.run(async () => {
            var criteria = new LadderProjectionDividendCriteria(dividendCriteriaBase, isAsset);
            const response = await this.pipelinesApiClient.getLadderProjectionDividends(criteria.toQueryObject())
            if (response.data.length > 0) {
                this.mergeProjections(this.pageManager.data, response.data, isAsset);
                this.notifications.addSuccess("Dividend Import Successful", "Dividends imported successfully.", NotificationDuration.Standard);
            }
            this.resetDividendCriteria();
        });
    }

    private mergeProjections(target: List<LadderProjection>, source: Array<Model.PlainObject<LadderProjectionDividendLookup>>, isAsset: boolean) {
        source.forEach((sourceLp) => {
            var existingProjection = target.find(lp => lp.isDividend && lp.isAsset === isAsset && lp.effectiveDate === sourceLp.dividendDate);
            if (existingProjection) {
                existingProjection.amount = sourceLp.totalDividend;
            }
            else {
                var newLp = target.addNew();
                newLp.isDividend = true;
                newLp.isAsset = isAsset;
                newLp.description = isAsset ? "Dividends Receivable" : "Dividends Payable";
                newLp.effectiveDate = new Date(sourceLp.dividendDate);
                newLp.amount = sourceLp.totalDividend;
            }
        });
    }

    public resetDividendCriteria() {
        this.investorDividendCriteria = null;
        this.issuerDividendCriteria = null;
    }
}